import React, { Fragment, useEffect, useMemo } from "react";
import { CircularProgress, Container, Typography, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ProductCard from "../components/ProductCard/ProductCard";
import { carouselData, categoryImages } from "../components/data/product";
import { useHomeProducts } from "../components/hooks/useHomeProducts";
import { Product } from "../types/Product";
import { AppConfigFields } from "../types/appConfigEntities";
import { HttpCache } from "../http/httpCache";
import { Icon, ImgPh, SectionHeader } from "../components/liquid/LiquidPrimitives";

type SnackbarNavState = {
  snackbar?: {
    message: string;
    variant?: "default" | "error" | "success" | "warning" | "info";
  };
};

type DiscountTier = {
  percent: string;
  threshold: string;
};

type BannerSlide = {
  type: "banner";
  autoplayDelayMs?: number;
  title: string;
  minPurchase: string;
  discountsTitle?: string;
  discounts: DiscountTier[];
  ctaText: string;
  locationText: string;
};

type ImageSlide = {
  type?: "image";
  autoplayDelayMs?: number;
  image: string;
  title: string;
  link: string;
};

type CarouselSlide = BannerSlide | ImageSlide;

type CategoryImage = {
  src: string;
  alt: string;
  title: string;
  link: string;
};

const normalizeProducts = (products: Product[], minCount: number): Product[] => {
  if (products.length === 0) return [];
  if (products.length >= minCount) return products;

  const normalized: Product[] = [...products];
  let i = 0;
  while (normalized.length < minCount) {
    normalized.push(products[i % products.length]);
    i++;
  }
  return normalized;
};

function getCarouselImagePath(imageName: string, isSmallScreen: boolean) {
  const dotIndex = imageName.lastIndexOf(".");
  if (dotIndex < 0) return `images/carousel-1/${imageName}`;
  const nameWithoutExtension = imageName.substring(0, dotIndex);
  const extension = imageName.substring(dotIndex);
  const smallSuffix = isSmallScreen ? "_small" : "";
  return `images/carousel-1/${nameWithoutExtension}${smallSuffix}${extension}`;
}

function HeroBannerSlide({ slide }: { slide: BannerSlide }) {
  const discounts = slide.discounts ?? window.APP_CONFIG?.[AppConfigFields.Discounts] ?? [];
  const minimumPurchase =
    slide.minPurchase ?? `$${Number(window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseAmount] ?? 150000).toLocaleString("es-AR")}`;

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 24px",
        background: `
          radial-gradient(900px 500px at 70% 0%, rgba(217, 194, 162, 0.7), transparent 65%),
          radial-gradient(700px 600px at 0% 100%, rgba(245, 237, 224, 0.9), transparent 60%),
          linear-gradient(160deg, #EFE3D0 0%, #E6D5BC 60%, #D9C2A2 100%)
        `,
      }}
    >
      <div className="lg-strong" style={{ width: "min(860px, 100%)", padding: "56px 64px", borderRadius: 36, textAlign: "center" }}>
        <div className="eyebrow" style={{ marginBottom: 20 }}>Mouchi</div>
        <h1 className="h-display" style={{ margin: 0, fontWeight: 500, textTransform: "uppercase" }}>
          {slide.title}
        </h1>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginTop: 28,
            padding: "10px 18px",
            borderRadius: 999,
            background: "rgba(10,10,10,0.06)",
            border: "1px solid rgba(0,0,0,0.05)",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Icon.bag />
          Compra minima {minimumPurchase}
        </div>

        <div style={{ marginTop: 28 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>{slide.discountsTitle || "Descuentos"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            {discounts.slice(0, 3).map((tier: any, index: number) => (
              <div
                key={`${tier.percent}-${tier.threshold}`}
                style={{
                  padding: 20,
                  borderRadius: 24,
                  background: index === 2 ? "var(--ink-900)" : "rgba(255,255,255,0.7)",
                  color: index === 2 ? "var(--cream-50)" : "var(--ink-900)",
                  border: "1px solid rgba(255,255,255,0.55)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85)",
                }}
              >
                <div className="mono" style={{ fontSize: 26, fontWeight: 600 }}>{tier.percent}</div>
                <div style={{ marginTop: 4, fontSize: 12, color: index === 2 ? "rgba(255,250,240,0.62)" : "var(--ink-500)" }}>
                  en compras mayores a {tier.threshold}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 30, flexWrap: "wrap" }}>
          <a className="btn-primary" href="/productos"><Icon.bag /> {slide.ctaText}</a>
          <span className="btn-ghost"><Icon.pin /> {slide.locationText}</span>
        </div>
      </div>
    </div>
  );
}

function HeroImageSlide({ slide, isSmallScreen }: { slide: ImageSlide; isSmallScreen: boolean }) {
  return (
    <a href={slide.link} style={{ display: "block", height: "100%", position: "relative", overflow: "hidden", color: "white" }}>
      <ImgPh
        label={slide.title}
        src={getCarouselImagePath(slide.image, isSmallScreen)}
        ratio="auto"
        brightness={0.68}
        style={{ height: "100%", borderRadius: 0, background: "linear-gradient(160deg, var(--cream-200), var(--cream-400))" }}
      />
      <div
        className="lg-dark"
        style={{
          position: "absolute",
          left: "50%",
          bottom: 44,
          transform: "translateX(-50%)",
          padding: "14px 20px",
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          textTransform: "uppercase",
          letterSpacing: "0.16em",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {slide.title} <Icon.arrowUpRight color="var(--cream-50)" />
      </div>
    </a>
  );
}

function HeroCarousel() {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const slides = carouselData as CarouselSlide[];

  return (
    <section style={{ padding: "32px 32px 48px", position: "relative", zIndex: 2 }}>
      <div
        style={{
          position: "relative",
          borderRadius: 40,
          overflow: "hidden",
          height: isSmallScreen ? 640 : 720,
          boxShadow: "0 40px 100px -40px rgba(60,40,20,0.35), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
      >
        <Swiper
          style={{ height: "100%" }}
          navigation={{
            nextEl: ".mouchi-hero-next",
            prevEl: ".mouchi-hero-prev",
          }}
          pagination={{
            clickable: true,
            el: ".mouchi-hero-pagination",
            bulletClass: "mouchi-hero-bullet",
            bulletActiveClass: "mouchi-hero-bullet-active",
          }}
          modules={[Navigation, Autoplay, Pagination]}
          loop
          autoplay={{ delay: 5000 }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index} data-swiper-autoplay={slide.autoplayDelayMs ? String(slide.autoplayDelayMs) : undefined}>
              {"type" in slide && slide.type === "banner" ? (
                <HeroBannerSlide slide={slide} />
              ) : (
                <HeroImageSlide slide={slide as ImageSlide} isSmallScreen={isSmallScreen} />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        <button className="pill mouchi-hero-prev" style={{ position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)", padding: 14, zIndex: 10 }}>
          <Icon.arrowLeft />
        </button>
        <button className="pill mouchi-hero-next" style={{ position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)", padding: 14, zIndex: 10 }}>
          <Icon.arrowRight />
        </button>
        <div className="mouchi-hero-pagination" style={{ position: "absolute", left: 0, right: 0, bottom: 22, display: "flex", justifyContent: "center", gap: 7, zIndex: 10 }} />
      </div>
    </section>
  );
}

function CategoryTile({ image, size }: { image: CategoryImage; size: "main" | "top" | "small" }) {
  return (
    <a
      href={image.link}
      style={{
        position: "relative",
        display: "block",
        height: "100%",
        overflow: "hidden",
        borderRadius: 28,
        background: "linear-gradient(160deg, var(--cream-100), var(--cream-300))",
        boxShadow: "0 14px 40px -14px rgba(60,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.5)",
      }}
    >
      <ImgPh label={image.alt} src={image.src} ratio="auto" brightness={0.72} style={{ height: "100%", borderRadius: 0 }} />
      <div
        className="lg"
        style={{
          position: "absolute",
          left: 16,
          right: 16,
          bottom: 16,
          padding: "16px 20px",
          borderRadius: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
        }}
      >
        <div style={{ fontSize: size === "main" ? 20 : 16, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>
          {image.title}
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: "var(--ink-900)",
            color: "var(--cream-50)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon.arrowUpRight color="var(--cream-50)" />
        </div>
      </div>
    </a>
  );
}

function CategoryShowcaseSection() {
  return (
    <section style={{ padding: "48px 32px", position: "relative", zIndex: 2 }}>
      <SectionHeader
        eyebrow="Categorias"
        title="Explora la coleccion"
        action={<a href="/productos" className="pill">Ver todas <Icon.arrowUpRight /></a>}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, height: 680 }}>
        <CategoryTile image={categoryImages.mainImage} size="main" />
        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 14, minHeight: 0 }}>
          <CategoryTile image={categoryImages.topImage} size="top" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, minHeight: 0 }}>
            <CategoryTile image={categoryImages.bottomLeftImage} size="small" />
            <CategoryTile image={categoryImages.bottomRightImage} size="small" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedSection({ products }: { products: Product[] }) {
  return (
    <section style={{ padding: "60px 32px", position: "relative", zIndex: 2 }}>
      <SectionHeader
        eyebrow="Destacados de la semana"
        title="Productos destacados"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button className="pill" style={{ padding: 12 }}><Icon.arrowLeft /></button>
            <button className="pill" style={{ padding: 12 }}><Icon.arrowRight /></button>
          </div>
        }
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
        {products.slice(0, 6).map((product, index) => (
          <ProductCard key={`${product.id}-${index}`} product={product} showDiscount={index === 0 || index === 2} discountText={index === 2 ? "Top" : "Nuevo"} />
        ))}
      </div>
    </section>
  );
}

function NovedadesSection({ products }: { products: Product[] }) {
  return (
    <section style={{ padding: "48px 32px 80px", position: "relative", zIndex: 2 }}>
      <div style={{ display: "grid", gridTemplateColumns: "0.95fr 2fr", gap: 18 }}>
        <div
          style={{
            position: "relative",
            borderRadius: 32,
            overflow: "hidden",
            minHeight: 540,
            background: "linear-gradient(165deg, #1B1815 0%, #0A0A0A 100%)",
            color: "var(--cream-50)",
            padding: "40px 36px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div style={{ position: "relative" }}>
            <div className="eyebrow" style={{ color: "var(--gold-soft)", marginBottom: 18 }}>Novedades</div>
            <h3 style={{ fontSize: 56, lineHeight: 0.95, margin: 0, fontWeight: 500, letterSpacing: "-0.035em" }}>
              Lo ultimo
              <br />
              en <em style={{ fontStyle: "normal", color: "var(--gold-soft)", fontFamily: '"New York", "Times New Roman", serif', fontWeight: 400 }}>oro y acero</em>
            </h3>
            <p style={{ fontSize: 14, color: "rgba(255,250,240,0.6)", marginTop: 18, lineHeight: 1.55, maxWidth: 320 }}>
              Descubri las piezas que acabamos de sumar al catalogo. Disenos frescos, minimos y atemporales.
            </p>
          </div>
          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="lg-dark" style={{ padding: "16px 20px", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13 }}>Nuevos ingresos</span>
              <span className="mono" style={{ fontWeight: 600 }}>{products.length}</span>
            </div>
            <a className="btn-ghost" href="/productos" style={{ color: "var(--cream-50)", justifyContent: "center" }}>
              Ver novedades <Icon.arrowUpRight />
            </a>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 18 }}>
          {products.slice(0, 4).map((product, index) => (
            <ProductCard key={`${product.id}-${index}`} product={product} showDiscount={index === 1} discountText="Nuevo" />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustRow() {
  const items = [
    { icon: <Icon.truck />, title: "Envios a todo el pais", sub: "Despachamos en 24hs" },
    { icon: <Icon.shield />, title: "Calidad garantizada", sub: "Acero quirurgico premium" },
    { icon: <Icon.bag />, title: "Compra mayorista", sub: "Minimo $150.000" },
    { icon: <Icon.spark />, title: "Stock permanente", sub: "+1.200 SKUs activos" },
  ];

  return (
    <section style={{ padding: "0 32px 60px", position: "relative", zIndex: 2 }}>
      <div className="lg-strong" style={{ padding: "28px 36px", borderRadius: 28, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
        {items.map((item, index) => (
          <div key={item.title} style={{ display: "flex", alignItems: "center", gap: 14, paddingRight: 24, borderRight: index < 3 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(10,10,10,0.06)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-900)" }}>
              {item.icon}
            </div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.01em" }}>{item.title}</div>
              <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 2 }}>{item.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const Home: React.FC = () => {
  const { showcaseProducts, novedadesProducts, loading, error } = useHomeProducts();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    HttpCache.getPersonTypes();
    HttpCache.getIdentityDocumentTypes();
  }, []);

  useEffect(() => {
    const state = location.state as SnackbarNavState | null;
    const snack = state?.snackbar;

    if (snack?.message) {
      enqueueSnackbar(snack.message, { variant: snack.variant ?? "default" });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [enqueueSnackbar, location.pathname, location.state, navigate]);

  const featured = useMemo(() => normalizeProducts(showcaseProducts, 6), [showcaseProducts]);
  const latest = useMemo(() => normalizeProducts(novedadesProducts, 4), [novedadesProducts]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Cargando productos...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4" color="error" gutterBottom>{error}</Typography>
        <Typography variant="body1" color="text.secondary">No pudimos cargar los productos en este momento.</Typography>
      </Container>
    );
  }

  return (
    <Fragment>
      <HeroCarousel />
      <CategoryShowcaseSection />
      {featured.length > 0 && <FeaturedSection products={featured} />}
      {latest.length > 0 && <NovedadesSection products={latest} />}
      <TrustRow />
    </Fragment>
  );
};

export default Home;
